import { useRef, useEffect, useCallback, forwardRef, useImperativeHandle, memo, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import classNames from 'classnames';
import styles from './FJAudioWave.module.less';
import FJMessageStore from '../../../store/FJMessageStore.js';
import FJLocalStore from '../../../store/FJLocalStore.js';
import FJUtil from '../../../util/FJUtil.js';

const FJAudioWave = (props, ref) => {
    const {
        style,
        className,
        url,
        config = {
            waveColor: '#a3a9b4',
            cursorColor: '#130d0d',
            interact: true,
            barWidth: 1,
            barGap: 2,
            cursorWidth: 2,
        },
        setIsPlay,
        updateDuration,
        recordPlay = () => {},
        isPlay,
    } = props;

    const waveRef = useRef(null);
    const _wave = useRef(null);
    const isLoadedRef = useRef(false);
    const isErrorRef = useRef(false);
    const [audioUrl, setAudioUrl] = useState(url);

    useImperativeHandle(ref, () => ({
        play: () => {
            play();
        },
        pause: () => {
            pause();
        },
        isLoaded: () => {
            return isLoadedRef.current;
        },
    }));

    useEffect(() => {
        setAudioUrl(url);
    }, [url]);

    useEffect(() => {
        isLoadedRef.current = false;
        isErrorRef.current = false;
        drawWave();
    }, [audioUrl]);

    const destroyWave = useCallback(() => {
        try {
            if (_wave.current) {
                _wave.current?.destroy();
                _wave.current = null;
            }
        } catch (e) {}
    }, []);

    const play = () => {
        if (isErrorRef.current && isLoadedRef.current) {
            setAudioUrl(FJUtil.getReloadNewUrl(url));
        } else {
            if (_wave.current) {
                _wave.current.play();
            }
        }
    };

    const pause = () => {
        if (_wave.current) {
            _wave.current.pause();
        }
    };

    const drawWave = useCallback(() => {
        destroyWave();
        if (audioUrl) {
            isErrorRef.current = false;
            isLoadedRef.current = false;
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const gradient = ctx.createLinearGradient(0, 0, 327, 0);
            gradient.addColorStop(0, '#FF47D0');
            gradient.addColorStop(1, '#FF822C');

            waveRef.current.innerHTML = '';
            _wave.current = WaveSurfer.create({
                container: waveRef.current,
                height: 20,
                ...config,
                progressColor: gradient,
            });
            _wave.current.on('finish', e => {
                setIsPlay(false);
                _wave.current.stop();
            });
            _wave.current.on('ready', e => {
                isLoadedRef.current = true;
                isErrorRef.current = false;
                updateDuration(_wave.current.getDuration());
                if (isPlay) {
                    _wave.current.play();
                }
            });

            _wave.current.on('error', e => {
                isErrorRef.current = true;
                isLoadedRef.current = true;
                setIsPlay(false);
                if (isPlay) {
                    FJMessageStore.addMessage(FJLocalStore._('playback-failed-please-try-again'), 'error');
                }
            });

            _wave.current.on('click', e => {
                setIsPlay(true);
                _wave.current.play();
                recordPlay();
            });

            _wave.current.load(audioUrl);
        }
    }, [audioUrl, config, destroyWave, isPlay]);

    const onClick = e => {
        e.stopPropagation();
    };

    return (
        <div className={styles.box} onClick={onClick}>
            <div style={style} ref={waveRef} className={classNames(styles.audio_wave, className)} />
        </div>
    );
};

export default memo(forwardRef(FJAudioWave));
